import { type JSX } from "react";
import { ScenarioSpawnNotification } from "./ScenarioSpawnNotification";
import { LineageStatsSection } from "./LineageStatsSection";
import { ScenarioRelationshipSection } from "./ScenarioRelationshipSection";
import { ConsistencyAlertSection } from "./ConsistencyAlertSection";
import { SubscriptionStatsSection } from "../universe/SubscriptionStatsSection";
import { useStatsFetch } from "../../api/hooks/useStatsFetch";
import { formatIsoToLocale } from "../../utils/formatIsoDate";
import type {
  AnchorViolationStatsResponse,
  ArchivedWorldlinesStatsResponse,
  ModerationStatsResponse,
  SceneInconsistencyStatsResponse,
  SceneSummaryStatsResponse,
  SpawnedFromResponse,
  SpawnedScenarioItem,
  SpawnsResponse,
  SpawnStatsResponse,
} from "../../api/types";

export type { SpawnedScenarioItem };

/**
 * scenario-spawn-creator-credit-ui (Phase γ' closure, Task 2.1) reader UI container。
 *
 * 既存 ScenarioSpawnNotification (= 上流通知) と新規 `GET /scenarios/:id/spawns`
 * (= 下流派生一覧) を 1 panel に統合し、 reader が双方向 lineage を 1 view で確認
 * できるようにする。 PlayerScreen の「あなたの物語の派生」 modal 中身として mount され、
 * panel 中身を 2 section に分ける:
 *
 *  上 = 上流通知 section (= 「派生元」 / ScenarioSpawnNotification reuse、 origin の場合は
 *       「派生元なし」 placeholder)
 *  下 = 下流派生一覧 section (= 「派生」 / paginated list / 0 件 placeholder)
 *
 * 派生元の有無を判定するため container 側でも `GET /scenarios/:id/spawned-from` を
 * 1 度 fetch する (= ScenarioSpawnNotification 側 fetch と並列、 idempotent GET の
 * 二重呼び出しは Phase γ' MVP で許容、 cache 共通化は future spec)。
 *
 * design.md §Component 2 / Req 2 / Req 3 対応。
 */

function resolveBaseUrl(): string {
  return (
    (import.meta.env.VITE_API_BASE_URL as string | undefined) ?? "/api/v1"
  );
}

const SPAWNS_LIMIT = 20;

export interface ScenarioLineagePanelProps {
  /** 通知対象の scenario id (= 当 scenario の上流 / 下流双方の lineage を fetch する key)。 */
  scenarioId: string;
  /** 表示用 scenario title (= 上流側 reuse component に pass through)。 */
  scenarioTitle: string;
  /**
   * universe-subscription (Phase ε 第 4 段): 当 scenario が紐づく universe の id を optional
   * で受ける。 渡された場合のみ SubscriptionStatsSection を additive 表示 (= 既存挙動 backward
   * compatible、 未指定の panel 呼出は section 非表示で touch ゼロ)。
   */
  universeId?: string;
}

export function ScenarioLineagePanel({
  scenarioId,
  scenarioTitle,
  universeId,
}: ScenarioLineagePanelProps): JSX.Element {
  // 上流 / 下流 fetch も sibling stats 群と同 shape (= data / isLoading / error) のため
  // useStatsFetch に集約。 旧 30+ 行 inline useEffect ×2 を hook 呼出 ×2 に置換。
  const upstream = useStatsFetch<SpawnedFromResponse>(
    `${resolveBaseUrl()}/scenarios/${encodeURIComponent(scenarioId)}/spawned-from`,
    "上流情報",
  );
  const downstream = useStatsFetch<SpawnsResponse>(
    `${resolveBaseUrl()}/scenarios/${encodeURIComponent(scenarioId)}/spawns?page=1&limit=${SPAWNS_LIMIT}`,
    "派生一覧",
  );

  // creator-tools-stats-shared (Phase δ' refactor): spawn-stats + archived-worldlines-stats を
  // useStatsFetch hook で共通化。 上流 / 下流 fetch は性質が異なるため touch ゼロで残す。
  const spawnStats = useStatsFetch<SpawnStatsResponse>(
    `${resolveBaseUrl()}/scenarios/${encodeURIComponent(scenarioId)}/spawn-stats`,
    "派生統計",
  );
  const archivedStats = useStatsFetch<ArchivedWorldlinesStatsResponse>(
    `${resolveBaseUrl()}/scenarios/${encodeURIComponent(scenarioId)}/archived-worldlines-stats`,
    "worldline 統計",
  );
  const moderationStats = useStatsFetch<ModerationStatsResponse>(
    `${resolveBaseUrl()}/scenarios/${encodeURIComponent(scenarioId)}/moderation-stats`,
    "moderation 統計",
  );
  const anchorViolationStats = useStatsFetch<AnchorViolationStatsResponse>(
    `${resolveBaseUrl()}/scenarios/${encodeURIComponent(scenarioId)}/anchor-violation-stats`,
    "anchor 違反統計",
  );
  const summaryStats = useStatsFetch<SceneSummaryStatsResponse>(
    `${resolveBaseUrl()}/scenarios/${encodeURIComponent(scenarioId)}/scene-summary-stats`,
    "scene 要約統計",
  );
  const inconsistencyStats = useStatsFetch<SceneInconsistencyStatsResponse>(
    `${resolveBaseUrl()}/scenarios/${encodeURIComponent(scenarioId)}/scene-inconsistency-stats`,
    "発言矛盾統計",
  );

  const isUpstreamDerived =
    upstream.data !== null && upstream.data.spawnedFromScenarioId !== null;
  const downstreamItems = downstream.data?.spawns ?? [];

  return (
    <div
      data-testid="scenario-lineage-panel"
      className="flex flex-col gap-4"
    >
      {/* 上流 section */}
      <section
        data-testid="scenario-lineage-panel-upstream-section"
        className="flex flex-col gap-2"
      >
        <h3 className="text-sm font-semibold text-reader-spawn-fg">派生元</h3>
        {isUpstreamDerived ? (
          <ScenarioSpawnNotification
            scenarioId={scenarioId}
            scenarioTitle={scenarioTitle}
          />
        ) : (
          // origin scenario / fetch 失敗 / 取得中 (= initial state) いずれの場合も
          // 「派生元なし」 placeholder で統一表示 (= reader 体験上 distinguish 不要)。
          <p
            data-testid="scenario-lineage-panel-upstream-empty"
            className="text-xs text-reader-spawn-fg opacity-70"
          >
            派生元なし
          </p>
        )}
      </section>

      {/* 下流 section */}
      <section
        data-testid="scenario-lineage-panel-downstream-section"
        className="flex flex-col gap-2"
      >
        <h3 className="text-sm font-semibold text-reader-spawn-fg">派生</h3>
        {downstream.isLoading ? (
          <p
            data-testid="scenario-lineage-panel-downstream-loading"
            className="text-xs text-reader-spawn-fg opacity-70"
          >
            派生情報を読み込み中…
          </p>
        ) : downstream.error !== null ? (
          // 取得失敗時は静かに非表示 (= reader 体験を妨げない、 best-effort)。
          // section header のみ残し、 list / placeholder は出さない。
          null
        ) : downstreamItems.length === 0 ? (
          <p
            data-testid="scenario-lineage-panel-downstream-empty"
            className="text-xs text-reader-spawn-fg opacity-70"
          >
            まだ派生はありません
          </p>
        ) : (
          <ul className="flex flex-col gap-1">
            {downstreamItems.map((item) => (
              <li
                key={item.id}
                data-testid={`scenario-lineage-panel-downstream-list-item-${item.id}`}
                className="text-sm"
              >
                <a
                  href={`/scenarios/${encodeURIComponent(item.id)}`}
                  className="block px-2 py-1 bg-reader-spawn-bg hover:bg-reader-spawn-bg-hover border border-reader-spawn-border rounded text-reader-spawn-fg"
                >
                  <span className="font-medium">{item.title}</span>
                  {/* scenario-spawn-promotion-reader-ui (Phase δ' M7 closure 第 3 段) Task 2.1:
                      visibility badge を additive 表示。 undefined のときは非表示で API v1
                      互換維持 (= 既存 client が新 field を ignore しても breakage しない)。 */}
                  {item.visibility !== undefined && (
                    <span
                      data-testid={`scenario-lineage-panel-downstream-visibility-${item.id}`}
                      className={
                        item.visibility === 'public'
                          ? 'inline-block text-[10px] px-1.5 py-0.5 ml-2 bg-reader-spawn-strong text-reader-spawn-strong-fg rounded'
                          : 'inline-block text-[10px] px-1.5 py-0.5 ml-2 bg-stone-700 text-stone-200 rounded'
                      }
                    >
                      {item.visibility === 'public' ? '公開' : '非公開'}
                    </span>
                  )}
                  <span className="block text-xs text-reader-spawn-fg opacity-60">
                    {formatIsoToLocale(item.spawnedAt) ?? item.spawnedAt}
                  </span>
                </a>
              </li>
            ))}
          </ul>
        )}
      </section>

      {/* creator-tools-spawn-stats (Task 3.1) summary section: 既存 上流 + 下流 list の下に additive。
          loading → indicator 表示 / error → 静かに非表示 / success → counts 表示。
          既存 上流 / 下流 / visibility badge は touch ゼロ。 */}
      <LineageStatsSection<SpawnStatsResponse>
        testIds={{
          section: "scenario-lineage-panel-spawn-stats-section",
          loading: "scenario-lineage-panel-spawn-stats-loading",
          stats: "scenario-lineage-panel-spawn-stats-stats",
        }}
        title="派生統計"
        loadingLabel="派生統計を読み込み中…"
        state={spawnStats}
        renderItems={(data) => (
          <>
            <li>派生試行: {data.totalAttempts}</li>
            <li>派生成立: {data.committed}</li>
            <li>公開昇格: {data.publicPromoted}</li>
            <li>
              trigger 別: hidden_ending {data.byTriggerType.hidden_ending}
              {" / "}memory_threshold {data.byTriggerType.memory_threshold}
              {" / "}rare_pattern {data.byTriggerType.rare_pattern}
            </li>
          </>
        )}
      />

      {/* creator-tools-archived-worldlines (Task 3.1) worldline 統計 section additive: 既存
          spawn 統計 section の直後に追加。 loading → indicator 表示 / error → 静かに非表示 /
          success → counts + ratio 表示。 既存 spawn 統計 section / 上流 / 下流 / visibility
          badge は touch ゼロ。 */}
      <LineageStatsSection<ArchivedWorldlinesStatsResponse>
        testIds={{
          section: "scenario-lineage-panel-archived-worldlines-section",
          loading: "scenario-lineage-panel-archived-worldlines-loading",
          stats: "scenario-lineage-panel-archived-worldlines-stats",
        }}
        title="worldline 統計"
        loadingLabel="worldline 統計を読み込み中…"
        state={archivedStats}
        renderItems={(data) => (
          <>
            <li>合計: {data.totalPlaylines}</li>
            <li>完結: {data.endedPlaylines}</li>
            <li>
              アーカイブ: {data.archivedPlaylines}
              {" "}({(data.archivedRatio * 100).toFixed(2)}%)
            </li>
          </>
        )}
      />

      {/* creator-tools-moderation-stats (Phase δ' 第 4 弾) moderation 統計 section additive:
          既存 worldline 統計 section の直後に追加。 loading → indicator 表示 / error → 静かに非表示 /
          success → counts + severity 別 + ratio 表示。 既存 上流 / 下流 / spawn 統計 / worldline 統計
          section / visibility badge は touch ゼロ。 */}
      <LineageStatsSection<ModerationStatsResponse>
        testIds={{
          section: "scenario-lineage-panel-moderation-section",
          loading: "scenario-lineage-panel-moderation-loading",
          stats: "scenario-lineage-panel-moderation-stats",
        }}
        title="moderation 統計"
        loadingLabel="moderation 統計を読み込み中…"
        state={moderationStats}
        renderItems={(data) => (
          <>
            <li>scene 数: {data.totalScenes}</li>
            <li>
              flagged: {data.totalFlagged}
              {" "}({(data.flaggedRatio * 100).toFixed(2)}%)
            </li>
            <li>
              severity: low {data.bySeverity.low}
              {" / "}medium {data.bySeverity.medium}
              {" / "}high {data.bySeverity.high}
            </li>
            {data.byCategory.length > 0 && (
              <li>
                category:{" "}
                {data.byCategory
                  .slice(0, 3)
                  .map((row) => `${row.category} ${row.count}`)
                  .join(" / ")}
              </li>
            )}
          </>
        )}
      />

      {/* creator-tools-anchor-violation-stats (Phase δ' 第 5 弾) anchor 違反統計 section additive:
          既存 moderation 統計 section の直後に追加。 byBeat 8 key は UI 表示省略 (= 詰込み回避、
          API response にはあり)、 byAnchorType + violationRate のみ表示。 既存 全 section touch ゼロ。 */}
      <LineageStatsSection<AnchorViolationStatsResponse>
        testIds={{
          section: "scenario-lineage-panel-anchor-violation-section",
          loading: "scenario-lineage-panel-anchor-violation-loading",
          stats: "scenario-lineage-panel-anchor-violation-stats",
        }}
        title="anchor 違反統計"
        loadingLabel="anchor 違反統計を読み込み中…"
        state={anchorViolationStats}
        renderItems={(data) => (
          <>
            <li>scene 数: {data.totalScenes}</li>
            <li>
              違反: {data.totalViolations}
              {" "}({(data.violationRate * 100).toFixed(2)}%)
            </li>
            <li>
              type: anchor {data.byAnchorType.anchor}
              {" / "}soft_anchor {data.byAnchorType.soft_anchor}
            </li>
            {data.byInvariant.length > 0 && (
              <li>
                invariant:{" "}
                {data.byInvariant
                  .slice(0, 3)
                  .map((row) => `${row.invariant} ${row.count}`)
                  .join(" / ")}
              </li>
            )}
          </>
        )}
      />

      {/* creator-tools-scene-summary-stats (Phase δ' 第 6 弾) scene 要約統計 section additive:
          既存 anchor 違反統計 section の直後に追加。 summaryRate は機構 M6 generation 成功率
          (= 1.0 が理想 health metric)、 avgLatencyMs は ms 単位。 既存 全 section touch ゼロ。 */}
      <LineageStatsSection<SceneSummaryStatsResponse>
        testIds={{
          section: "scenario-lineage-panel-scene-summary-section",
          loading: "scenario-lineage-panel-scene-summary-loading",
          stats: "scenario-lineage-panel-scene-summary-stats",
        }}
        title="scene 要約統計"
        loadingLabel="scene 要約統計を読み込み中…"
        state={summaryStats}
        renderItems={(data) => (
          <>
            <li>scene 数: {data.totalScenes}</li>
            <li>
              要約: {data.totalSummaries}
              {" "}({(data.summaryRate * 100).toFixed(2)}%)
            </li>
            <li>平均 latency: {data.avgLatencyMs} ms</li>
          </>
        )}
      />

      {/* creator-tools-scene-inconsistency-stats (Phase δ' 第 7 弾) 発言矛盾統計 section additive:
          既存 scene 要約統計 section の直後に追加。 byInconsistencyType は上位 3 type のみ表示
          (= 詰込み回避、 future polish 余地)、 既存 全 section touch ゼロ。 */}
      <LineageStatsSection<SceneInconsistencyStatsResponse>
        testIds={{
          section: "scenario-lineage-panel-scene-inconsistency-section",
          loading: "scenario-lineage-panel-scene-inconsistency-loading",
          stats: "scenario-lineage-panel-scene-inconsistency-stats",
        }}
        title="発言矛盾統計"
        loadingLabel="発言矛盾統計を読み込み中…"
        state={inconsistencyStats}
        renderItems={(data) => (
          <>
            <li>scene 数: {data.totalScenes}</li>
            <li>
              矛盾: {data.totalInconsistencies}
              {" "}({(data.inconsistencyRate * 100).toFixed(2)}%)
            </li>
            <li>平均 latency: {data.avgLatencyMs} ms</li>
            {data.byInconsistencyType.length > 0 && (
              <li>
                type:{" "}
                {data.byInconsistencyType
                  .slice(0, 3)
                  .map((row) => `${row.type} ${row.count}`)
                  .join(" / ")}
              </li>
            )}
          </>
        )}
      />

      {/* scenario-relationship (Phase ε 機構 M9 第 4 sub-spec) Task 9.2:
          関連シナリオ section (= creator declare relationship 双方向 list)。 */}
      <ScenarioRelationshipSection scenarioId={scenarioId} />

      {/* cross-layer-consistency-check (Phase ε 第 5 spec) Task 12:
          整合性アラート section (= AI suggest only doctrine、 pending alert minimal 表示)。 */}
      <ConsistencyAlertSection scenarioId={scenarioId} />

      {/* universe-subscription (Phase ε 第 4 段) Task 9.2:
          universe 別 subscription stats section (= optional universeId prop で additive、
          未指定の panel 呼出は非表示で既存挙動 backward compatible)。 */}
      {universeId !== undefined ? (
        <SubscriptionStatsSection universeId={universeId} />
      ) : null}
    </div>
  );
}
